import {
  camelToSpacedCase,
  isEmail,
  isEntityRef,
  isISODate,
  isURL,
} from '@agilelab/plugin-wb-platform-common';
import { parseEntityRef } from '@mainstage/catalog-model';
import {
  Children,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
} from 'react';
import { get } from 'lodash';
import { CustomViewComponent } from './CustomViewComponent';
import { CustomViewRegister } from './registry';
import { NUNJUCKS_PROPS } from './constants';

/**
 * Takes a root ReactNode and resolves all "include" types by using the provided includeResolver function, directly replacing them with their jsx
 */
export const resolveIncludesInJsx = (
  root: ReactNode,
  context: {
    typeId?: string;
    templateId?: string;
    includeResolver: (
      id: string,
      typeId: string,
      templateId: string,
    ) => ReactElement<any> | undefined;
  },
): ReactElement<any> => {
  const traverse = (element: ReactNode): ReactElement<any> => {
    const { typeId, templateId, includeResolver } = context;

    if (!isValidElement<any>(element))
      throw new Error('Invalid Custom View element');

    const props = element.props;

    // Handle include type - resolve and return the included content
    if (props.type === 'include' && props.id) {
      const includedContent = includeResolver(
        props.id,
        typeId ?? '',
        templateId ?? '',
      );
      if (includedContent) {
        // Recursively resolve includes within the included content
        return traverse(includedContent);
      }
      // If include cannot be resolved, return null or keep the include element
      return element;
    }

    // Clone element with traversed children
    const children =
      'children' in props ? Children.toArray(props.children) : [];

    if (children.length === 0) {
      return element;
    }

    const traversedChildren = Children.map(children, child => traverse(child));

    return cloneElement(element, undefined, ...traversedChildren);
  };

  return traverse(root);
};

/**
 * Extracts all the nunjucks templates used in the custom view,
 * including "include" and components, in order to load them and make them available for rendering
 */
export const extractNunjucksTemplates = (root: ReactElement): Set<string> => {
  const templates = new Set<string>();

  const traverse = (element: ReactNode): void => {
    if (!isValidElement(element))
      throw new Error('Invalid Custom View element');

    const props = element.props;

    // Recursively traverse children
    if ('children' in props) {
      const children = Children.toArray(props.children);
      children.forEach(child => traverse(child));
    }

    // this could be a fragment, return if that's the case
    if (!props.type) return;

    const extraNunjucksTemplates =
      CustomViewRegister.getNunjucksPropsPaths(props.type) ?? [];

    const nunjucksTemplatesToExtract =
      extraNunjucksTemplates.concat(NUNJUCKS_PROPS);
    nunjucksTemplatesToExtract.forEach(propPath => {
      const resolved = get(props, propPath);

      // If value is a string (expression), add it to the set
      if (typeof resolved === 'string') {
        templates.add(resolved);
      }
    });
  };

  traverse(root);
  return templates;
};

const isProfile = (value: string) =>
  isEntityRef(value) &&
  (parseEntityRef(value).kind === 'user' ||
    parseEntityRef(value).kind === 'group');

export const addHlines = (
  list: ReactElement[],
  hlineEveryNElements: number,
) => {
  if (hlineEveryNElements <= 0) return list;
  const result: ReactElement[] = [];
  let current = 0;
  list.forEach((e: ReactElement, i: number) => {
    const colSpan = Number(e.props.colSpan || '1');
    const totSpan = colSpan + current;
    if (totSpan >= hlineEveryNElements) {
      // if the current element overflows the available space:
      // add a divider, than the current will be the colSpan of the overflowed element
      if (totSpan > hlineEveryNElements) {
        result.push(<CustomViewComponent type="hline" />);
        current = colSpan;
      }
      result.push(e);
      // in the case the element did not overflow, but it is perfectly fitting all the space:
      // just push the divider and set the current to 0, as there is no new elements
      if (totSpan === hlineEveryNElements) {
        result.push(<CustomViewComponent key={i} type="hline" />);
        current = 0;
      }
    } else {
      current += colSpan;
      result.push(e);
    }
  });
  return result;
};

/**
 * Retrieves custom view information from an object.
 * @param obj - The object to extract custom view information from.
 * @param [path=''] - The path to prepend to each extracted key.
 * @param [options] - Additional options for customization.
 * @param [enableFilterProperties=true] - Whether to enable filtering properties.
 * @returns An array of JSX elements representing custom view components.
 */
export const getCustomViewInfoFromObject = (
  obj: Record<string, any>,
  path: string = '',
  options?: Record<string, any>,
  enableFilterProperties: boolean = true,
): ReactElement[] => {
  const excludeList = options?._exclude || [];
  const opts = options ?? {};
  const priority = (options?.priority || ['name', 'description']).map(
    (p: string) => p.toLowerCase(),
  );
  return addHlines(
    Object.entries(obj ?? {})
      .sort(([a], [b]) => {
        const pa = priority.indexOf(a.toLowerCase());
        const pb = priority.indexOf(b.toLowerCase());
        if (pa > pb) return 1;
        if (pa < pb) return -1;
        return 0;
      })
      .filter(([label, _]) => excludeList.indexOf(label) < 0)
      .filter(
        ([label, value]) =>
          !enableFilterProperties ||
          typeof value === 'string' ||
          typeof value === 'boolean' ||
          typeof value === 'number' ||
          label in opts,
      )
      .map(([key, value]) => {
        const objectOptions = Object.assign(
          {},
          opts._default || { colSpan: '1' },
          opts[key] || {},
        );
        const extraProps: Record<string, any> = objectOptions;
        if (isURL(String(value || ''))) extraProps.href = value;
        if (isEmail(String(value || ''))) extraProps.href = `mailto:${value}`;
        let type = 'string';
        if (isISODate(String(value || ''))) type = 'date';
        if (isProfile(String(value || ''))) type = 'profile';
        let label = camelToSpacedCase(key);
        // remove "__" for internal fields
        if (label.startsWith('__')) label = label.slice(2);
        return (
          <CustomViewComponent
            key={key}
            label={label}
            path={path === '' ? key : `${path}.${key}`}
            type={objectOptions.type || type}
            {...extraProps}
            // we disable nunjucks processing here since fields are dynamic and their templates are not loaded
            __disableNunjucksProcessing
          />
        );
      }),
    opts.hlineEveryNElements || 0,
  );
};

export const getChildren = (
  props: Record<string, any>,
): ReactElement<any>[] => {
  if (!props.children) return [];
  return Children.toArray(props.children).filter(isValidElement);
};

export const extractIncludeFromCode = (
  root: ReactElement,
  defTypeId: string = '',
  defTemplateId: string = '',
): Array<any> => {
  if (!root) return [];
  return (
    root.props.type === 'include'
      ? [
          {
            id: root.props.id,
            typeId: root.props.typeId || defTypeId || '',
            templateId: root.props.templateId || defTemplateId || '',
          },
        ]
      : []
  ).concat(
    getChildren(root.props).flatMap((e: ReactElement) =>
      extractIncludeFromCode(
        e,
        root.props.typeId || defTypeId || '',
        root.props.templateId || defTemplateId || '',
      ),
    ),
  );
};
